import React from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import MicroclimateAnalysis from "./MicroclimateAnalysis";

const ClimateChart = ({ title, data, dataKey }) => {
  if (!data || !data.data || data.data.length === 0) {
    return <div>No data available</div>;
  }

  // Format the data for the chart
  const chartData = data.data.map((item) => {
    // Create month-day label for x-axis
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const monthName = monthNames[item.month - 1];

    return {
      date: `${monthName} ${item.day}`,
      min: item.min,
      max: item.max,
      mean: item.mean,
      dayOfYear: item.day_of_year,
      month: item.month,
      range: item.max - item.min,
    };
  });

  // Get every month for the X-axis ticks
  const tickValues = chartData
    .filter((item) => item.day === 15) // Middle of each month
    .map((item) => item.date.split(" ")[0]); // Only month name

  // Determine comfort zone based on data type
  let comfortZone = {};
  if (dataKey === "temperature") {
    comfortZone = {
      min: 18,
      max: 26,
      label: "ASHRAE adaptive comfort (80%)",
    };

    // Add a second comfort zone for 90%
    comfortZone.second = {
      min: 20,
      max: 24,
      label: "ASHRAE adaptive comfort (90%)",
    };
  } else if (dataKey === "humidity") {
    comfortZone = {
      min: 30,
      max: 70,
      label: "Humidity comfort band",
    };
  }

  // Determine chart colors based on data type
  const colors =
    dataKey === "temperature"
      ? {
          range: "#FF0000",
          mean: "#FF0000",
          comfort: "#DDDDDD",
          comfort2: "#CCCCCC",
        }
      : {
          range: "#0000FF",
          mean: "#0000FF",
          comfort: "#DDDDDD",
        };

  // Calculate Y-axis range based on data
  const minValue =
    Math.floor(Math.min(...chartData.map((d) => d.min)) / 10) * 10;
  const maxValue =
    Math.ceil(Math.max(...chartData.map((d) => d.max)) / 10) * 10;

  // Calculate Heating Degree Days (HDD) and Cooling Degree Days (CDD)
  const calculateDegreeDays = () => {
    if (dataKey !== "temperature") return null;

    const baseTemp = 18; // Base temperature in Celsius for HDD/CDD calculations
    let hdd = 0;
    let cdd = 0;

    chartData.forEach((day) => {
      // For HDD: if mean temperature is below base temperature
      if (day.mean < baseTemp) {
        hdd += baseTemp - day.mean;
      }
      // For CDD: if mean temperature is above base temperature
      if (day.mean > baseTemp) {
        cdd += day.mean - baseTemp;
      }
    });

    return {
      hdd: Math.round(hdd),
      cdd: Math.round(cdd),
    };
  };

  // Calculate Diurnal Temperature Range (DTR)
  const calculateDTR = () => {
    if (dataKey !== "temperature") return null;

    // Average daily temperature range
    const avgDTR =
      chartData.reduce((sum, day) => sum + day.range, 0) / chartData.length;

    // Monthly DTR
    const monthlyDTR = Array(12)
      .fill(0)
      .map((_, i) => {
        const monthData = chartData.filter((day) => day.month === i + 1);
        if (monthData.length === 0) return 0;
        return (
          monthData.reduce((sum, day) => sum + day.range, 0) / monthData.length
        );
      });

    return {
      average: avgDTR.toFixed(1),
      monthly: monthlyDTR.map((val) => val.toFixed(1)),
      monthNames: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ],
    };
  };

  // Calculate Annual and Seasonal Extreme Temperatures
  const calculateExtremeTemps = () => {
    if (dataKey !== "temperature") return null;

    // Annual extremes
    const annualMax = Math.max(...chartData.map((day) => day.max));
    const annualMin = Math.min(...chartData.map((day) => day.min));

    // Seasonal extremes
    const seasons = {
      winter: { months: [12, 1, 2], max: -100, min: 100 },
      spring: { months: [3, 4, 5], max: -100, min: 100 },
      summer: { months: [6, 7, 8], max: -100, min: 100 },
      fall: { months: [9, 10, 11], max: -100, min: 100 },
    };

    // Calculate seasonal extremes
    chartData.forEach((day) => {
      Object.keys(seasons).forEach((season) => {
        if (seasons[season].months.includes(day.month)) {
          seasons[season].max = Math.max(seasons[season].max, day.max);
          seasons[season].min = Math.min(seasons[season].min, day.min);
        }
      });
    });

    return {
      annual: {
        max: annualMax.toFixed(1),
        min: annualMin.toFixed(1),
        range: (annualMax - annualMin).toFixed(1),
      },
      seasonal: {
        winter: {
          max: seasons.winter.max.toFixed(1),
          min: seasons.winter.min.toFixed(1),
        },
        spring: {
          max: seasons.spring.max.toFixed(1),
          min: seasons.spring.min.toFixed(1),
        },
        summer: {
          max: seasons.summer.max.toFixed(1),
          min: seasons.summer.min.toFixed(1),
        },
        fall: {
          max: seasons.fall.max.toFixed(1),
          min: seasons.fall.min.toFixed(1),
        },
      },
    };
  };

  // Calculate comfort metrics
  const calculateComfortMetrics = () => {
    if (dataKey !== "temperature") return null;

    // Count days within comfort zones
    const comfortDays80 = chartData.filter(
      (day) => day.mean >= comfortZone.min && day.mean <= comfortZone.max
    ).length;

    const comfortDays90 = chartData.filter(
      (day) =>
        day.mean >= comfortZone.second.min && day.mean <= comfortZone.second.max
    ).length;

    // Calculate percentage of days in comfort zones
    const comfortPercent80 = ((comfortDays80 / chartData.length) * 100).toFixed(
      1
    );
    const comfortPercent90 = ((comfortDays90 / chartData.length) * 100).toFixed(
      1
    );

    // Count extreme days
    const extremeHotDays = chartData.filter((day) => day.max > 35).length;
    const extremeColdDays = chartData.filter((day) => day.min < 0).length;

    return {
      comfortDays80,
      comfortDays90,
      comfortPercent80,
      comfortPercent90,
      extremeHotDays,
      extremeColdDays,
    };
  };

  // Generate interpretation text based on calculated values
  const generateInterpretation = (
    degreeDays,
    dtr,
    extremeTemps,
    comfortMetrics
  ) => {
    if (dataKey !== "temperature") return null;

    // Heating and cooling needs interpretation
    let heatingCoolingText = "";
    if (degreeDays.hdd > 2500) {
      heatingCoolingText = "This location has significant heating requirements";
    } else if (degreeDays.hdd > 1000) {
      heatingCoolingText = "This location has moderate heating requirements";
    } else {
      heatingCoolingText = "This location has minimal heating requirements";
    }

    if (degreeDays.cdd > 1000) {
      heatingCoolingText += " and substantial cooling needs.";
    } else if (degreeDays.cdd > 400) {
      heatingCoolingText += " and moderate cooling needs.";
    } else {
      heatingCoolingText += " and minimal cooling needs.";
    }

    // DTR interpretation
    let dtrText = "";
    const avgDTR = parseFloat(dtr.average);
    if (avgDTR > 15) {
      dtrText =
        "The large diurnal temperature range (DTR) indicates significant temperature swings between day and night, which may require adaptive building strategies to maintain comfort.";
    } else if (avgDTR > 10) {
      dtrText =
        "The moderate diurnal temperature range (DTR) suggests noticeable temperature differences between day and night.";
    } else {
      dtrText =
        "The small diurnal temperature range (DTR) indicates relatively stable temperatures throughout the day and night.";
    }

    // Seasonal variation interpretation
    const annualRange = parseFloat(extremeTemps.annual.range);
    let seasonalText = "";
    if (annualRange > 40) {
      seasonalText =
        "This location experiences extreme seasonal temperature variations, requiring highly adaptable building systems.";
    } else if (annualRange > 25) {
      seasonalText =
        "This location has significant seasonal temperature variations, requiring flexible building systems.";
    } else if (annualRange > 15) {
      seasonalText =
        "This location has moderate seasonal temperature variations.";
    } else {
      seasonalText =
        "This location has relatively stable temperatures throughout the year.";
    }

    // Comfort interpretation
    let comfortText = "";
    if (parseFloat(comfortMetrics.comfortPercent80) > 70) {
      comfortText = `The climate is naturally comfortable for ${comfortMetrics.comfortPercent80}% of the year (within ASHRAE 80% acceptability limits), suggesting good potential for passive design strategies.`;
    } else if (parseFloat(comfortMetrics.comfortPercent80) > 40) {
      comfortText = `The climate is naturally comfortable for ${comfortMetrics.comfortPercent80}% of the year (within ASHRAE 80% acceptability limits), indicating moderate potential for passive design strategies.`;
    } else {
      comfortText = `The climate is naturally comfortable for only ${comfortMetrics.comfortPercent80}% of the year (within ASHRAE 80% acceptability limits), suggesting active systems will be needed for comfort.`;
    }

    // Extreme conditions
    let extremeText = "";
    if (
      comfortMetrics.extremeHotDays > 30 &&
      comfortMetrics.extremeColdDays > 30
    ) {
      extremeText =
        "The location experiences both significant hot and cold extremes, requiring robust HVAC systems.";
    } else if (comfortMetrics.extremeHotDays > 30) {
      extremeText =
        "The location experiences significant hot extremes, requiring effective cooling strategies.";
    } else if (comfortMetrics.extremeColdDays > 30) {
      extremeText =
        "The location experiences significant cold extremes, requiring effective heating strategies.";
    } else if (
      comfortMetrics.extremeHotDays > 10 ||
      comfortMetrics.extremeColdDays > 10
    ) {
      extremeText =
        "The location experiences occasional temperature extremes that should be considered in building design.";
    } else {
      extremeText = "The location rarely experiences extreme temperatures.";
    }

    return {
      heatingCoolingText,
      dtrText,
      seasonalText,
      comfortText,
      extremeText,
    };
  };

  // Get the calculated values
  const degreeDays = calculateDegreeDays();
  const dtr = calculateDTR();
  const extremeTemps = calculateExtremeTemps();
  const comfortMetrics = calculateComfortMetrics();
  const interpretation =
    dataKey === "temperature"
      ? generateInterpretation(degreeDays, dtr, extremeTemps, comfortMetrics)
      : null;

  return (
    <div className="climate-chart">
      <h2>{title}</h2>
      <div className="chart-container">
        <ResponsiveContainer width="100%" height={400}>
          <ComposedChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 30 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              ticks={tickValues}
              tickFormatter={(value) => value.split(" ")[0]} // Only show month
              label={{
                value: "Month",
                position: "insideBottomRight",
                offset: -10,
              }}
            />
            <YAxis
              domain={[minValue, maxValue]}
              label={{
                value: data.variable.unit,
                angle: -90,
                position: "insideLeft",
              }}
            />
            <Tooltip
              formatter={(value, name) => {
                if (name === "Dry bulb temperature Range") {
                  return [`${value.toFixed(1)} ${data.variable.unit}`, name];
                }
                return [`${value.toFixed(1)} ${data.variable.unit}`, name];
              }}
              labelFormatter={(label, items) => {
                const item = items[0]?.payload;
                return item ? `Day ${item.dayOfYear}: ${label}` : label;
              }}
            />
            <Legend />

            {dataKey === "temperature" && (
              <>
                {/* ASHRAE adaptive comfort (80%) */}
                <Bar
                  dataKey={() => comfortZone.max - comfortZone.min}
                  stackId="comfort"
                  barSize={365}
                  name={comfortZone.label}
                  fill={colors.comfort}
                  fillOpacity={0.5}
                  isAnimationActive={false}
                  legendType="rect"
                  baseValue={comfortZone.min}
                />

                {/* ASHRAE adaptive comfort (90%) */}
                <Bar
                  dataKey={() =>
                    comfortZone.second.max - comfortZone.second.min
                  }
                  stackId="comfort2"
                  barSize={365}
                  name={comfortZone.second.label}
                  fill={colors.comfort2}
                  fillOpacity={0.5}
                  isAnimationActive={false}
                  legendType="rect"
                  baseValue={comfortZone.second.min}
                />
              </>
            )}

            {dataKey === "humidity" && (
              <Bar
                dataKey={() => comfortZone.max - comfortZone.min}
                stackId="comfort"
                barSize={365}
                name={comfortZone.label}
                fill={colors.comfort}
                fillOpacity={0.3}
                isAnimationActive={false}
                legendType="rect"
                baseValue={comfortZone.min}
              />
            )}

            {/* Temperature/Humidity range bar */}
            <Bar
              dataKey="range"
              stackId="range"
              barSize={365}
              name={`${data.variable.name} Range`}
              fill={colors.range}
              fillOpacity={0.3}
              isAnimationActive={false}
              baseValue={(d) => d.min}
            />

            {/* Mean line */}
            <Line
              type="monotone"
              dataKey="mean"
              stroke={colors.mean}
              dot={false}
              name={`Average ${data.variable.name}`}
              strokeWidth={2}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="chart-info">
        <p>
          <strong>Location:</strong> {data.location.city},{" "}
          {data.location.country}
        </p>
        <p>
          <strong>Variable:</strong> {data.variable.name} ({data.variable.unit})
        </p>
      </div>

      {/* Temperature-specific calculations */}
      {dataKey === "temperature" && degreeDays && dtr && extremeTemps && (
        <div className="temperature-analysis">
          <div className="analysis-section">
            <h3>Thermal Comfort Analysis</h3>

            <div className="analysis-grid">
              <div className="analysis-card">
                <h4>Degree Days</h4>
                <div className="degree-days-content">
                  <div className="degree-day-item">
                    <span className="degree-day-label">
                      Heating Degree Days (HDD):
                    </span>
                    <span className="degree-day-value">
                      {degreeDays.hdd} °C·days
                    </span>
                    <span className="degree-day-desc">
                      Base temperature: 18°C
                    </span>
                  </div>
                  <div className="degree-day-item">
                    <span className="degree-day-label">
                      Cooling Degree Days (CDD):
                    </span>
                    <span className="degree-day-value">
                      {degreeDays.cdd} °C·days
                    </span>
                    <span className="degree-day-desc">
                      Base temperature: 18°C
                    </span>
                  </div>
                </div>
              </div>

              <div className="analysis-card">
                <h4>Diurnal Temperature Range (DTR)</h4>
                <div className="dtr-content">
                  <p>
                    <strong>Annual Average DTR:</strong> {dtr.average}°C
                  </p>
                  <div className="monthly-dtr">
                    <p>
                      <strong>Monthly DTR:</strong>
                    </p>
                    <div className="monthly-dtr-grid">
                      {dtr.monthNames.map((month, index) => (
                        <div key={month} className="monthly-dtr-item">
                          <span className="month-name">{month}:</span>
                          <span className="month-value">
                            {dtr.monthly[index]}°C
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="analysis-card">
                <h4>Extreme Temperatures</h4>
                <div className="extremes-content">
                  <div className="extreme-annual">
                    <p>
                      <strong>Annual:</strong> Max: {extremeTemps.annual.max}°C
                      | Min: {extremeTemps.annual.min}°C | Range:{" "}
                      {extremeTemps.annual.range}°C
                    </p>
                  </div>
                  <div className="extreme-seasonal">
                    <p>
                      <strong>Seasonal Extremes:</strong>
                    </p>
                    <div className="seasonal-grid">
                      <div className="season-extreme">
                        <span className="season-name">Winter:</span>
                        <span className="season-values">
                          Max: {extremeTemps.seasonal.winter.max}°C | Min:{" "}
                          {extremeTemps.seasonal.winter.min}°C
                        </span>
                      </div>
                      <div className="season-extreme">
                        <span className="season-name">Spring:</span>
                        <span className="season-values">
                          Max: {extremeTemps.seasonal.spring.max}°C | Min:{" "}
                          {extremeTemps.seasonal.spring.min}°C
                        </span>
                      </div>
                      <div className="season-extreme">
                        <span className="season-name">Summer:</span>
                        <span className="season-values">
                          Max: {extremeTemps.seasonal.summer.max}°C | Min:{" "}
                          {extremeTemps.seasonal.summer.min}°C
                        </span>
                      </div>
                      <div className="season-extreme">
                        <span className="season-name">Fall:</span>
                        <span className="season-values">
                          Max: {extremeTemps.seasonal.fall.max}°C | Min:{" "}
                          {extremeTemps.seasonal.fall.min}°C
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Interpretation Section */}
            {interpretation && (
              <div className="interpretation-section">
                <h4>Climate Interpretation & Design Implications</h4>
                <div className="interpretation-content">
                  <p>{interpretation.heatingCoolingText}</p>
                  <p>{interpretation.dtrText}</p>
                  <p>{interpretation.seasonalText}</p>
                  <p>{interpretation.comfortText}</p>
                  <p>{interpretation.extremeText}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add the MicroclimateAnalysis component */}
      <MicroclimateAnalysis data={data} dataType={dataKey} />
    </div>
  );
};

export default ClimateChart;
